import { computed } from "vue";
import { useStore } from "vuex";
import { type ExportFormatType } from "shared-utils/enums/exports";
import { TemplateType } from "shared-utils/enums/masterTemplate";

import { useDownloadHTMLVideo } from "@/composables/download/useDownloadHTMLVideo";
import { useDownloadHTMLZip } from "@/composables/download/useDownloadHTMLZip";
import { useDownloadFlashtalkingExcel } from "@/composables/download/useDownloadFlashtalkingExcel";
import { useDownloadAEVideo } from "@/composables/download/useDownloadAEVideo";
import { useDownloadInDesign } from "@/composables/download/useDownloadInDesign";
import type { RootState } from "@/store/types";

export function useDownload() {
    const store = useStore<RootState>();
    const campaignType = computed(() => store.state.campaign.campaignType ?? TemplateType.HTML5);

    const {
        computeQueryParams: computeQueryParamsVideo,
        resetDownloadOptions: resetVideoDownloadOptions,
        isButtonDisabled: isButtonDisabledHTMLVideo
    } = useDownloadHTMLVideo();

    const {
        computeQueryParams: computeQueryParamsHTMLZip,
        resetDownloadOptions: resetDownloadOptionsHTMLZip,
        isButtonDisabled: isButtonDisabledHTMLZip
    } = useDownloadHTMLZip();

    const {
        computeQueryParams: computeFlashTalkingExcelParams,
        resetDownloadOptions: resetFlashTalkingExcelOptions,
        isButtonDisabled: isButtonDisabledFlashTalkingExcel
    } = useDownloadFlashtalkingExcel();

    const { isButtonDisabled: isButtonDisabledAEVideo, resetValues: resetAEVideoValues } = useDownloadAEVideo();

    const { computeQueryParams: computeQueryParamsInDesign, resetDownloadOptions: resetInDesignDownloadOptions } =
        useDownloadInDesign();

    function resetDownloadOptions() {
        resetDownloadOptionsHTMLZip();
        resetFlashTalkingExcelOptions();
        resetVideoDownloadOptions();
        resetAEVideoValues();
        resetInDesignDownloadOptions();
    }

    function computeQueryParams(exportFormat: ExportFormatType) {
        return {
            ...computeQueryParamsHTMLZip(exportFormat),
            ...computeFlashTalkingExcelParams(exportFormat),
            ...computeQueryParamsVideo(exportFormat),
            ...computeQueryParamsInDesign(exportFormat)
        };
    }

    function getIsButtonDisabled(exportFormat: ExportFormatType | "") {
        if (exportFormat === "") {
            return true;
        }

        if (campaignType.value === TemplateType.AE) {
            return isButtonDisabledAEVideo.value;
        }

        if (campaignType.value === TemplateType.HTML5) {
            return (
                isButtonDisabledHTMLZip.value[exportFormat] ??
                isButtonDisabledFlashTalkingExcel.value[exportFormat] ??
                isButtonDisabledHTMLVideo.value[exportFormat] ??
                false
            );
        }

        return false;
    }

    return {
        getIsButtonDisabled,
        resetDownloadOptions,
        computeQueryParams
    };
}
